import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    Chip,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import ShareIcon from '@mui/icons-material/Share';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
    get_wallet_transactions,
    generate_coupon,
    get_coupon_list,
    delete_coupon,
    get_profile,
    get_coupon_invoice_download
} from '../../../api/ControllApi';
import { useSettings } from '../../../context/Settings';

const PurchaseCoupon = () => {
    const navigate = useNavigate();
    const { settings } = useSettings();
    const [amount, setAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('razorpay');
    const [loading, setLoading] = useState(false);
    const [balanceLoading, setBalanceLoading] = useState(true);
    const [walletTransactions, setWalletTransactions] = useState([]);
    const [userProfile, setUserProfile] = useState(null);

    const [coupons, setCoupons] = useState([]);
    const [couponLoading, setCouponLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedCouponId, setSelectedCouponId] = useState(null);

    useEffect(() => {
        fetchBalance();
        fetchProfile();
        fetchCoupons(1);
    }, []);

    const fetchBalance = async () => {
        try {
            setBalanceLoading(true);
            const res = await get_wallet_transactions();
            if (res && res.status) {
                setWalletTransactions(res.data || []);
            }
        } catch (err) {
        } finally {
            setBalanceLoading(false);
        }
    };

    const fetchProfile = async () => {
        try {
            const res = await get_profile();
            if (res && res.status) {
                setUserProfile(res.data);
            }
        } catch (err) {
        }
    };

    const fetchCoupons = async (pageNum) => {
        try {
            setCouponLoading(true);
            const res = await get_coupon_list(pageNum);
            if (res && res.status) {
                setCoupons(res.data.data || []);
                setTotalPages(res.data.last_page || 1);
            }
        } catch (err) {
            toast.error('Failed to load coupon list');
        } finally {
            setCouponLoading(false);
        }
    };

    const handleCopy = (code) => {
        navigator.clipboard.writeText(code);
        toast.success('Code copied to clipboard!');
    };

    const handleShare = (coupon) => {
        const purchaseDate = coupon.created_at ? new Date(coupon.created_at).toLocaleDateString() : 'N/A';
        let expiresDateStr = coupon.expiry_date || coupon.expires_at;
        let expiresDateObj = null;
        if (expiresDateStr) {
            const normalizedDate = expiresDateStr.includes('T') ? expiresDateStr : expiresDateStr.replace(' ', 'T');
            expiresDateObj = new Date(normalizedDate);
        }
        if ((!expiresDateObj || isNaN(expiresDateObj.getTime())) && coupon.created_at) {
            expiresDateObj = new Date(coupon.created_at);
            expiresDateObj.setFullYear(expiresDateObj.getFullYear() + 1);
        }
        const expiryDate = (expiresDateObj && !isNaN(expiresDateObj.getTime()))
            ? expiresDateObj.toLocaleDateString()
            : 'N/A';

        const shareText = `🎉 Gift Voucher Alert! 🎉\n\nYou've received a gift voucher worth ₹${parseFloat(coupon.amount).toFixed(0)}!\n\n🔐 Voucher Code: ${coupon.name}\n\n📅 Purchase Date: ${purchaseDate}\n\n⏳ Expiry Date: ${expiryDate}\n\nEnjoy your gift! 🎁`;

        if (navigator.share) {
            navigator.share({ title: 'Gift Voucher Alert!', text: shareText }).catch(() => { });
        } else {
            navigator.clipboard.writeText(shareText);
            toast.success('Coupon details copied to clipboard!');
        }
    };

    const gstPercent = parseFloat(settings?.data?.coupon_service_charge || 0);
    const gstAmount = parseFloat(amount || 0) * gstPercent / 100;
    const totalWithGst = parseFloat(amount || 0) + gstAmount;

    const handlePurchase = async () => {
        const numAmount = Number(amount);
        if (!amount || isNaN(numAmount) || numAmount < 100 || numAmount > 500000) {
            toast.error('Amount must be between 100 and 500,000');
            return;
        }

        if (paymentMethod === 'wallet') {
            if (Number(userProfile?.wallet_balance || 0) < totalWithGst) {
                toast.error('Insufficient wallet balance');
                return;
            }
            submitPurchase({ wallet: true, amount: parseFloat(amount), gst: gstPercent });
        } else {
            initiateRazorpay();
        }
    };

    const initiateRazorpay = async () => {
        const loadRazorpay = () => {
            return new Promise((res) => {
                if (window.Razorpay) return res(window.Razorpay);
                const script = document.createElement("script");
                script.src = "https://checkout.razorpay.com/v1/checkout.js";
                script.onload = () => res(window.Razorpay);
                script.onerror = () => res(null);
                document.body.appendChild(script);
            });
        };

        const Razorpay = await loadRazorpay();
        if (!Razorpay) {
            toast.error('Razorpay SDK not loaded');
            return;
        }

        const options = {
            key: settings?.data?.payment_api_key || process.env.REACT_APP_RAZOR_PAY,
            amount: totalWithGst * 100,
            currency: "INR",
            name: "Cashback Farms",
            description: "Purchase Coupon",
            image: "/assets/icons/__Fav Icon.png",
            handler: async function (response) {
                const payload = {
                    amount: parseFloat(amount),
                    transaction_id: response.razorpay_payment_id,
                    payment_method: "razorpay",
                    gst: gstPercent,
                    user_gst: userProfile?.gst || ""
                };
                submitPurchase(payload);
            },
            prefill: {
                name: userProfile?.name || "",
                email: userProfile?.email || "",
                contact: userProfile?.phone || ""
            },
            theme: { color: "#3B6019" },
        };

        const rzp = new Razorpay(options);
        rzp.open();
    };

    const submitPurchase = async (payload) => {
        setLoading(true);
        try {
            const res = await generate_coupon(payload);
            if (res && res.status) {
                toast.success(res.message || 'Coupon generated successfully!');
                setAmount('');
                if (payload.wallet) {
                    await fetchBalance();
                    await fetchProfile();
                }
                fetchCoupons(1);
            } else {
                toast.error(res?.message || 'Failed to generate coupon');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                maxWidth: 560,
                mx: 'auto',
                bgcolor: '#ffffff',
                borderRadius: '24px',
                border: '1px solid #E6EFE3',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.02)',
                p: { xs: 3, md: 4 },
                my: 2,
            }}
        >
            {/* Title */}
            <Typography
                variant="h6"
                sx={{
                    fontWeight: 700,
                    color: '#3B6019',
                    textAlign: 'center',
                    fontSize: '20px',
                    mb: 3,
                }}
            >
                Gift / Payment Coupons
            </Typography>

            {/* Payment Method Toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
                <Button
                    onClick={() => setPaymentMethod('razorpay')}
                    sx={{
                        borderRadius: '24px',
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '14px',
                        border: paymentMethod === 'razorpay' ? '1px solid #5B9E00' : '1px solid #D8D8D8',
                        bgcolor: paymentMethod === 'razorpay' ? '#ffffff' : '#ffffff',
                        color: paymentMethod === 'razorpay' ? '#000000' : '#444444',
                        boxShadow: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': {
                            bgcolor: '#f9f9f9',
                        },
                    }}
                >
                    <span style={{ fontWeight: 800, italic: 'italic', color: '#0A2540', fontSize: '15px' }}>
                        Razorpay
                    </span>
                </Button>

                <Button
                    onClick={() => setPaymentMethod('wallet')}
                    sx={{
                        borderRadius: '24px',
                        px: 3.5,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '14px',
                        border: 'none',
                        bgcolor: paymentMethod === 'wallet' ? '#5B9E00' : '#F0F0F0',
                        color: paymentMethod === 'wallet' ? '#ffffff' : '#444444',
                        boxShadow: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': {
                            bgcolor: paymentMethod === 'wallet' ? '#4e8a00' : '#e0e0e0',
                        },
                    }}
                >
                    <AccountBalanceWalletIcon sx={{ fontSize: 18 }} />
                    Wallet
                </Button>
            </Box>

            {/* Wallet Balance Display */}
            {paymentMethod === 'wallet' && (
                <Box
                    sx={{
                        bgcolor: '#F5F9F3',
                        border: '1px solid #D5E2CE',
                        borderRadius: '16px',
                        p: 2,
                        mb: 2.5,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography sx={{ fontWeight: 600, color: '#3B6019', fontSize: '13px' }}>
                        Your Wallet Balance
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: '#2E5B10', fontSize: '16px' }}>
                        {userProfile ? `₹${Number(userProfile.wallet_balance || 0).toFixed(2)}` : 'Loading...'}
                    </Typography>
                </Box>
            )}

            {/* Amount Input Section */}
            <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#2B4A10', mb: 1 }}>
                Enter Coupon Amount
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid #D5E2CE',
                    borderRadius: '24px',
                    height: 48,
                    px: 2.5,
                    bgcolor: '#ffffff',
                    '&:focus-within': {
                        borderColor: '#5B9E00',
                        boxShadow: '0 0 0 2px rgba(91,158,0,0.15)',
                    },
                }}
            >
                <Typography sx={{ fontWeight: 700, color: '#3B6019', mr: 1.5, fontSize: '16px' }}>₹</Typography>
                <input
                    type="number"
                    placeholder="Enter the Coupon amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    style={{
                        border: 'none',
                        outline: 'none',
                        width: '100%',
                        fontSize: '14px',
                        color: '#333333',
                        background: 'transparent',
                    }}
                />
            </Box>
            <Typography sx={{ fontSize: '11px', color: '#888888', mt: 0.5, pl: 1 }}>
                enter 100 to 500000
            </Typography>

            {/* Proceed Button */}
            <Button
                onClick={handlePurchase}
                disabled={loading || !amount || (paymentMethod === 'wallet' && totalWithGst > Number(userProfile?.wallet_balance || 0))}
                sx={{
                    width: '100%',
                    mt: 2.5,
                    height: 48,
                    borderRadius: '24px',
                    bgcolor: '#2E5B10',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '15px',
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': {
                        bgcolor: '#22460B',
                    },
                    '&.Mui-disabled': {
                        bgcolor: '#CCCCCC',
                        color: '#ffffff',
                    },
                }}
            >
                {loading ? <CircularProgress size={22} color="inherit" /> : 'Proceed'}
            </Button>

            {/* Divider */}
            <Divider sx={{ my: 3, borderColor: '#EEF4EC' }} />

            {/* My Coupons Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ConfirmationNumberOutlinedIcon sx={{ color: '#3B6019', fontSize: 20 }} />
                <Typography sx={{ fontWeight: 700, color: '#3B6019', fontSize: '15px' }}>
                    My Coupons
                </Typography>
            </Box>

            {/* Coupon List */}
            {couponLoading && coupons.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={28} sx={{ color: '#3B6019' }} />
                </Box>
            ) : (
                <>
                    <Box sx={{ maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
                        {coupons.length > 0 ? (
                            coupons.map((coupon) => {
                                let daysLeft = '0d Left';
                                let expiresDateStr = coupon.expiry_date || coupon.expires_at;
                                if (expiresDateStr) {
                                    const expDate = new Date(expiresDateStr.includes('T') ? expiresDateStr : expiresDateStr.replace(' ', 'T'));
                                    if (!isNaN(expDate.getTime())) {
                                        const diffTime = expDate - new Date();
                                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                        daysLeft = diffDays > 0 ? `${diffDays}d Left` : '0d Left';
                                    }
                                }

                                const percentUsed = coupon.status === 1 ? '100% Use' : '0% Use';

                                return (
                                    <Box
                                        key={coupon.id}
                                        sx={{
                                            border: '1px solid #E2EBE0',
                                            borderRadius: '18px',
                                            p: 2,
                                            mb: 2,
                                            bgcolor: '#ffffff',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.015)',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                            <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#222222', letterSpacing: '0.5px' }}>
                                                {coupon.name}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleCopy(coupon.name)}
                                                    sx={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: '50%',
                                                        bgcolor: '#F7FAF6',
                                                        border: '1px solid #E0EBE0',
                                                        color: '#3B6019',
                                                        '&:hover': { bgcolor: '#EBF4E8' },
                                                    }}
                                                >
                                                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleShare(coupon)}
                                                    sx={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: '50%',
                                                        bgcolor: '#F7FAF6',
                                                        border: '1px solid #E0EBE0',
                                                        color: '#3B6019',
                                                        '&:hover': { bgcolor: '#EBF4E8' },
                                                    }}
                                                >
                                                    <QrCodeScannerIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                                            <Chip
                                                label={daysLeft}
                                                size="small"
                                                sx={{
                                                    bgcolor: '#FFF3E0',
                                                    color: '#E65100',
                                                    fontWeight: 600,
                                                    fontSize: '11px',
                                                    borderRadius: '12px',
                                                    height: 24,
                                                }}
                                            />
                                            <Chip
                                                label={percentUsed}
                                                size="small"
                                                sx={{
                                                    bgcolor: '#E8F5E9',
                                                    color: '#2E7D32',
                                                    fontWeight: 600,
                                                    fontSize: '11px',
                                                    borderRadius: '12px',
                                                    height: 24,
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                );
                            })
                        ) : (
                            <Typography sx={{ color: '#888888', textAlign: 'center', py: 3, fontSize: '14px' }}>
                                No coupons found.
                            </Typography>
                        )}
                    </Box>

                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(e, v) => { setPage(v); fetchCoupons(v); }}
                                color="success"
                                size="small"
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default PurchaseCoupon;